import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { feedbackTemplatesAPI, placementSettingsAPI } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FileText, Edit, Trash2, Star, Settings, Copy } from 'lucide-react';
import { toast } from 'sonner';

const FeedbackTemplates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({ assessments_per_placement: 4 });
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ assessments_per_placement: 4 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, settingsRes] = await Promise.all([
        feedbackTemplatesAPI.getAll(),
        placementSettingsAPI.get()
      ]);
      setTemplates(templatesRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await feedbackTemplatesAPI.delete(id);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await placementSettingsAPI.update(settingsForm);
      toast.success('Settings saved');
      setShowSettings(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const newTemplate = {
        ...template,
        name: `${template.name} (Copy)`,
        is_default: false
      };
      delete newTemplate.id;
      delete newTemplate.created_at;
      delete newTemplate.updated_at;
      
      await feedbackTemplatesAPI.create(newTemplate);
      toast.success('Template duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-500">Admin access required</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="feedback-templates-page" className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Feedback Templates</h1>
            <p className="text-gray-500 mt-1">Manage assessment form templates for placements</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button variant="outline" onClick={() => setShowSettings(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button onClick={() => navigate('/feedback-templates/new')} >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>

        {/* Settings Summary */}
        <Card className="bg-ica-navy/5 border-ica-navy/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-ica-navy/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-ica-navy" />
                </div>
                <div>
                  <p className="font-medium text-ica-navy">Assessments per Placement</p>
                  <p className="text-sm text-gray-600">
                    Each placement requires <strong>{settings.assessments_per_placement}</strong> feedback forms
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
                Change
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500 text-center mb-4">
                No templates yet. Create your first feedback template to get started.
              </p>
              <Button onClick={() => navigate('/feedback-templates/new')} >
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-ica-gold/10 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-ica-gold" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-ica-navy flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <Star className="w-4 h-4 text-ica-gold fill-ica-gold" />
                          )}
                        </CardTitle>
                        {template.is_default && (
                          <span className="text-xs text-ica-gold">Default Template</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {template.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
                  )}
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">{template.elements?.length || 0}</span> assessment criteria
                  </div>
                  <div className="text-xs text-gray-400">
                    Updated: {formatDate(template.updated_at)}
                  </div>
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/feedback-templates/${template.id}`)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDuplicate(template)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(template.id)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Settings Modal */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">Placement Settings</DialogTitle>
              <DialogDescription className="text-gray-500">
                Configure how many feedback assessments are required per placement.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Assessments per Placement</Label>
                <Select
                  value={settingsForm.assessments_per_placement?.toString()}
                  onValueChange={(value) => setSettingsForm({ ...settingsForm, assessments_per_placement: parseInt(value) })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Assessment</SelectItem>
                    <SelectItem value="2">2 Assessments</SelectItem>
                    <SelectItem value="3">3 Assessments</SelectItem>
                    <SelectItem value="4">4 Assessments (1st, 2nd, 3rd, Final)</SelectItem>
                    <SelectItem value="5">5 Assessments</SelectItem>
                    <SelectItem value="6">6 Assessments</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-2">
                  Each placement will require this many feedback forms from the chef.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings} >
                Save Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FeedbackTemplates;
